import { useState } from "react";
import { Link } from "react-router-dom";
import { authService } from "@/services/api";
import { AuthShell, Field } from "./AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.requestReset(email);
      setSent(true);
    } catch (err) {
      // Only reachable if the request itself failed (offline, 5xx). The
      // endpoint answers 200 for unknown addresses too, so a failure here
      // genuinely means "couldn't ask", not "no such account" — and the
      // difference matters, otherwise someone whose network dropped would sit
      // waiting for an email that was never requested.
      setError(err.response?.data?.error || "Could not send the reset link. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell testId="forgot-page">
      {sent ? (
        <div>
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-emerald-400/25 bg-emerald-400/10 text-emerald-400">
            <MailCheck className="h-6 w-6" />
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Check your inbox</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            If an account exists for <span className="text-foreground">{email || "that email"}</span>, we've sent a reset link.
            It expires in 1 hour and can only be used once.
          </p>
          <Link to="/login"><Button variant="secondary" className="mt-6"><ArrowLeft className="mr-1.5 h-4 w-4" /> Back to sign in</Button></Link>
        </div>
      ) : (
        <>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Reset your password</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">Enter your work email and we'll send a reset link.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Work email">
              <Input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(null); }} placeholder="you@company.com" required className="h-11" data-testid="forgot-email" />
            </Field>
            {error && (
              <div className="rounded-xl border border-red-500/25 bg-red-500/10 px-3 py-2 text-sm text-red-300" data-testid="forgot-error">
                {error}
              </div>
            )}
            <Button type="submit" className="h-11 w-full" disabled={loading} data-testid="forgot-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
            </Button>
          </form>
          <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
        </>
      )}
    </AuthShell>
  );
}
